const G = (path, group, title, intro, steps, tips = []) => ({ path, group, title, intro, steps, tips });

export const GUIDES = [
  G("/dashboard", "Start here", "Overview", "Your home page. It sums up your own campaigns, creators and next actions. It starts empty and fills in as you work.", [
    "Start with Find Creators to look up TikTok or Instagram creators and save the good ones.",
    "Create a campaign, then attach saved creators. Reach, engagements and earned media value are calculated from them.",
    "Next actions lists draft campaigns without creators and calendar posts waiting for review this week.",
    "Trending now shows creators gaining attention. Open Trending to load a category.",
  ], ["Every number here comes from your own data. Nothing is filled in for you."]),
  G("/discovery", "Creator Marketing", "Find Creators", "Search for creators by @handle, keyword, hashtag or location and save them to your CRM.", [
    "Type an @handle and press Enter for a live profile with followers, engagement and recent posts.",
    "Use Discover to search by keyword or hashtag, location and minimum audience size.",
    "Open a creator to see audience quality, estimated rate and content, then Save to CRM.",
  ], ["Live lookups use your plan's monthly searches. The counter shows how many are left."]),
  G("/trending", "Creator Marketing", "Trending", "Creators gaining attention right now, ranked by a trending score.", [
    "Pick a category and platform (TikTok, Instagram or both). Add a city or country if you like.",
    "Open a profile in a new tab, or save the creator to your CRM.",
  ]),
  G("/crm", "Creator Marketing", "Creator CRM", "Every creator you saved, with a status, tags and notes.", [
    "Move creators through Prospect, Contacted, Negotiating, Active and Partner.",
    "Add tags and private notes so your team knows the history.",
    "Search by name or handle to find someone quickly.",
  ]),
  G("/campaigns", "Creator Marketing", "Campaigns", "Plan a campaign, attach creators and track results.", [
    "New campaign: give it a name, goal, platforms, budget, dates and brief.",
    "Open the campaign and attach creators from your CRM. It goes Live and its numbers update.",
    "Change the earned media rate to match how your team values an engagement.",
  ]),
  G("/clients", "Creator Marketing", "Clients", "Private client spaces with a shareable, passcode-protected report.", [
    "Add a client, then choose which campaigns and finished videos belong to them.",
    "Add your agency logo and colours, and let AI write a summary and highlights.",
    "Share the report link with a passcode, or Print / Save as PDF.",
  ]),
  G("/outreach", "Creator Marketing", "AI Outreach", "Write a personal message to a creator and send it by email.", [
    "Fill in the creator, campaign, tone and what you want them to make.",
    "Generate a draft, edit it, then add the creator's email and Send.",
    "Sent messages appear in the history with the date.",
  ], ["Replies go straight to your own email address."]),
  G("/listening", "Creator Marketing", "Social Listening", "Follow topics, brands or competitors across TikTok and Instagram.", [
    "New monitor: give it a name and the keywords or hashtags to follow.",
    "Refresh pulls the latest posts and shows mentions, weekly change, tone, top posts and top creators.",
  ], ["A refresh uses 2 of your monthly searches (one per platform)."]),
  G("/calendar", "Creator Marketing", "Content Calendar", "See and plan when creator posts go out.", [
    "Add a post with a title, date, platform, status and campaign.",
    "Use the status (Scheduled, In review, Revision, Approved, Live) to track approvals.",
    "Posts waiting for review this week also show in Overview → Next actions.",
  ]),
  G("/analytics", "Creator Marketing", "Analytics", "Results across all your campaigns.", [
    "Reach, engagements and earned media value are added up from your campaigns.",
    "The 6-month trend and channel mix fill in once campaigns have creators attached.",
    "Generate executive insights asks the AI analyst for the strongest signals and next moves.",
  ]),
  G("/studio", "AI Video Studio", "Studio Home", "Start a new AI video or continue a project.", [
    "New Video: type a project name (and pick a brand kit) and press Create Video.",
    "Each project moves through 01 Idea → 02 Review → 03 Generate → 04 Download.",
    "Before anything is made you see the exact models and the maximum credits, and you approve them.",
    "Videos keep being made on the server if you leave the app.",
  ], ["Stuck on Checking status? Press Restart stuck scene. Finished scenes are kept and you are not charged twice."]),
  G("/studio/projects", "AI Video Studio", "Video Project", "Turn an idea, a song or your own footage into a finished video.", [
    "Idea: choose a mode (create from an idea, replace a character, talking presenter, edit my footage). Add product details, photos and music.",
    "Chat with the Director to shape the story, or let it write a story from your lyrics.",
    "Review: edit every scene, its length, sound and model. You can approve each starting image before any video is made.",
    "Generate: follow each step live. Stop, Restart stuck scene and Continue this video are always available.",
    "Download: preview, download the MP4 or make a new version. Finished scenes are reused for free.",
  ], ["See Studio Guide for the full walkthrough with screenshots."]),
  G("/studio/media", "AI Video Studio", "Media Library", "All your uploaded and generated pictures and videos, grouped by project.", [
    "Upload product photos, character photos and clips to reuse in any project.",
    "Download or reuse a finished image as a scene's starting frame.",
  ]),
  G("/studio/motion-transfer", "AI Video Studio", "Motion Transfer", "Copy the movement from one video onto your own character.", [
    "Upload or link a video with the movement you want.",
    "Add a clear photo of your character, review the price and generate.",
  ]),
  G("/studio/brand-kits", "AI Video Studio", "Brand Kits", "Save your logo, colours and fonts so every video looks on-brand.", [
    "Create a kit with your logo, colours and text style. Mark one as default.",
    "Pick the kit when you start a new video. Its look is used for titles and end cards.",
  ]),
  G("/studio/credits", "AI Video Studio", "Credits & Usage", "Your Studio credit balance and history.", [
    "See your balance, credits reserved for running videos and every charge.",
    "You only pay for what is used; unused reserved credits are released automatically.",
    "Add credits from Plans → Credit packs.",
  ]),
  G("/studio/help", "AI Video Studio", "Studio Guide", "The full illustrated guide to the AI Studio.", ["Scroll through each step, or copy the whole guide as text."]),
  G("/creators", "Creator Marketing", "Creator Profile", "Everything about one creator: audience, content, rates and fit.", [
    "Check followers, engagement, audience quality and brand safety before you reach out.",
    "Refresh pulls the latest live numbers (uses one monthly search).",
    "Save to CRM, then attach the creator to a campaign or write outreach.",
  ]),
  G("/admin", "Account", "Admin", "Manage users, payments and leads (administrators only).", [
    "Users: change a user's role or plan, or remove an account.",
    "Payments: revenue and every transaction.",
    "Leads: messages sent from the public contact form.",
  ]),
  G("/payment", "Account", "Payment", "The result of a plan or credit purchase.", [
    "A successful payment updates your plan or credits right away.",
    "If you cancelled, nothing was charged. Go back to Plans & Billing to try again.",
  ]),
  G("/plans", "Account", "Plans & Billing", "Your plan, monthly search limits and Studio credit packs.", [
    "Compare plans and upgrade with a card. Your plan changes as soon as payment is confirmed.",
    "Buy Studio credit packs for AI videos.",
  ]),
  G("/settings", "Account", "Account Settings", "Your profile details and account options.", [
    "Check your name, email, company and plan.",
    "Delete account: enter your password and type DELETE. Your Studio credits are lost and any subscription is cancelled.",
  ], ["Stop any running video before deleting your account."]),
  G("/help", "Account", "Help Center", "Guides for every section of HypeFlow AI.", ["Pick a section to read its guide. On any page, the Guide button at the top opens the guide for that page."]),
];

export const guideFor = pathname =>
  [...GUIDES].sort((a, b) => b.path.length - a.path.length).find(g => pathname === g.path || pathname.startsWith(g.path + "/")) || GUIDES[0];
